import {Injectable} from '@angular/core';
import {CdkVirtualScrollViewport, VirtualScrollStrategy} from '@angular/cdk/scrolling';
import {Subject} from 'rxjs';

@Injectable()
export class EstimatedHeightVirtualScrollStrategy implements VirtualScrollStrategy {
    private viewport: CdkVirtualScrollViewport | null = null;
    private resizeObserver: ResizeObserver | null = null;
    private itemOffsets: number[] = [0];
    private totalContentSize = 0;
    private contentPadding = 0;
    private lastRenderedIndex = 0;
    private itemHeightFn: (index: number) => number = () => 42;
    private itemCountFn: () => number = () => 0;
    private readonly minBufferPx = 200;
    private readonly maxBufferPx = 400;
    private readonly scrolledIndexChangeSubject = new Subject<number>();

    readonly scrolledIndexChange = this.scrolledIndexChangeSubject.asObservable();

    setHeightFunctions(
        itemHeightFn: (index: number) => number,
        itemCountFn: () => number,
    ): void {
        this.itemHeightFn = itemHeightFn;
        this.itemCountFn = itemCountFn;
        this.updateItemHeights();
    }

    setContentPadding(contentPadding: number): void {
        this.contentPadding = contentPadding;
        this.updateTotalContentSize();
        this.updateRenderedRange();
    }

    attach(viewport: CdkVirtualScrollViewport): void {
        this.viewport = viewport;

        this.resizeObserver?.disconnect();
        this.resizeObserver = new ResizeObserver(() => {
            this.updateRenderedRange();
        });
        this.resizeObserver.observe(viewport.elementRef.nativeElement);

        this.updateTotalContentSize();
        this.updateRenderedRange();
    }

    detach(): void {
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
        this.viewport = null;
    }

    onContentScrolled(): void {
        this.updateRenderedRange();
    }

    onDataLengthChanged(): void {
        this.updateTotalContentSize();
        this.updateRenderedRange();
    }

    onContentRendered(): void {
    }

    onRenderedOffsetChanged(): void {
    }

    scrollToIndex(index: number, behavior: ScrollBehavior): void {
        if (!this.viewport) return;
        this.viewport.scrollToOffset(this.getOffsetForIndex(index), behavior);
    }

    updateItemHeights(): void {
        if (!this.viewport) return;
        this.updateTotalContentSize();
        this.updateRenderedRange();

        // Re-run after Angular applies DOM updates to avoid stale viewport/content-wrapper sizes.
        Promise.resolve().then(() => {
            if (!this.viewport) return;
            this.updateTotalContentSize();
            this.updateRenderedRange();
        });
    }

    private updateTotalContentSize(): void {
        if (!this.viewport) return;

        const count = this.itemCountFn();
        if (!count) {
            this.itemOffsets = [0];
            this.totalContentSize = 0;
            this.viewport.setTotalContentSize(this.totalContentSize + this.contentPadding);
            return;
        }

        this.itemOffsets = new Array(count + 1);
        let offset = 0;

        for (let i = 0; i < count; i += 1) {
            this.itemOffsets[i] = offset;
            offset += this.itemHeightFn(i);
        }

        this.itemOffsets[count] = offset;
        this.totalContentSize = offset;
        this.viewport.setTotalContentSize(this.totalContentSize + this.contentPadding);
    }

    private updateRenderedRange(): void {
        if (!this.viewport) return;

        const viewportSize = this.viewport.elementRef.nativeElement.clientHeight || this.viewport.getViewportSize();

        // If all content fits within viewport, render everything from the top
        if (this.totalContentSize <= viewportSize) {
            const range = {start: 0, end: this.itemCountFn()};
            this.viewport.setRenderedRange(range);
            this.viewport.setRenderedContentOffset(0);

            if (this.lastRenderedIndex !== 0) {
                this.lastRenderedIndex = 0;
                this.scrolledIndexChangeSubject.next(0);
            }
            return;
        }

        const scrollOffset = this.viewport.measureScrollOffset();
        const startOffset = Math.max(0, scrollOffset - this.minBufferPx);
        const endOffset = scrollOffset + viewportSize + this.maxBufferPx;

        const startIndex = this.findIndexForOffset(startOffset);
        const endIndex = Math.min(this.itemCountFn(), this.findIndexForOffset(endOffset) + 1);
        const range = {start: startIndex, end: endIndex};

        this.viewport.setRenderedRange(range);

        const startOffsetForIndex = this.getOffsetForIndex(startIndex);
        const endOffsetForIndex = this.getOffsetForIndex(endIndex);
        const renderedContentSize = Math.max(0, endOffsetForIndex - startOffsetForIndex);
        const maxOffset = Math.max(0, this.totalContentSize - renderedContentSize);

        this.viewport.setRenderedContentOffset(Math.min(startOffsetForIndex, maxOffset));

        if (startIndex !== this.lastRenderedIndex) {
            this.lastRenderedIndex = startIndex;
            this.scrolledIndexChangeSubject.next(startIndex);
        }
    }

    private getOffsetForIndex(index: number): number {
        if (index <= 0) return 0;
        if (index >= this.itemOffsets.length) return this.itemOffsets[this.itemOffsets.length - 1] ?? 0;
        return this.itemOffsets[index] ?? 0;
    }

    private findIndexForOffset(offset: number): number {
        if (!this.itemOffsets.length) return 0;

        let low = 0;
        let high = this.itemOffsets.length - 1;

        while (low < high) {
            const mid = Math.floor((low + high) / 2);
            if (this.itemOffsets[mid] === offset) return mid;
            if (this.itemOffsets[mid] < offset) {
                low = mid + 1;
            } else {
                high = mid;
            }
        }

        return Math.max(0, low - 1);
    }
}
